import React, { FC, Fragment } from 'react';
import { Trans } from 'react-i18next';
import { FieldArrayRenderProps, useFormikContext } from 'formik';
import { Card, CardContent, Divider, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ContentInput } from 'api';
import { PostAction, UserGroup } from 'types/app';
import { Body } from 'features/content/widgets';
import { hasVideo } from 'features/content/helpers';
import { ValidatedSwitch } from 'components';
import { isAdminUser } from 'helpers';
import { useCurrentUser } from 'hooks';

import { AdminCustomization, Header, MarkImportant, PinPost, PostButton } from './EditorPartials';
import Preview from './Preview';

interface IProps {
  working: boolean;
  groups: UserGroup[];
  onClose: () => void;
  onGroupToggle: (group: UserGroup, arrayHelper: FieldArrayRenderProps) => void;
  selectedGroup: UserGroup;
}

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  headerText: {
    paddingLeft: theme.spacing(2),
  },
  contentActions: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: 0,
  },
  contentOptions: {
    paddingBottom: 0,
  },
  dividerActions: {
    marginTop: theme.spacing(1),
  },
  dividerButton: {
    marginBottom: theme.spacing(2),
  },
  spacer: {
    margin: theme.spacing(0.5, 0),
  },
}));

const ManualEditor: FC<IProps> = ({ onClose, groups, onGroupToggle, working, selectedGroup }) => {
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const { values, resetForm, isValid } = useFormikContext<ContentInput>();

  function handleClose() {
    resetForm();
    onClose();
  }

  function renderOptions() {
    const isVideoPost = hasVideo(values);

    if (isVideoPost) {
      return null;
    }

    return (
      <Grid container alignItems="center" justify="flex-end">
        <Grid item>
          <ValidatedSwitch
            name="is_shareable"
            color="primary"
            label={<Trans i18nKey="posting:shareable" />}
          />
        </Grid>
      </Grid>
    );
  }

  function renderAdminOptions() {
    if (!isAdminUser(currentUser)) {
      return null;
    }

    return (
      <Fragment>
        <Divider />
        <div className={classes.spacer}>
          <PinPost />
        </div>
        <Divider />
        <div className={classes.spacer}>
          <MarkImportant />
        </div>
      </Fragment>
    );
  }

  function renderAdminCustomization() {
    if (!isAdminUser(currentUser)) {
      return null;
    }

    return (
      <Fragment>
        <Divider />
        <CardContent className={classes.contentOptions}>
          <AdminCustomization allowUtm />
        </CardContent>
      </Fragment>
    );
  }

  return (
    <Card className={classes.root}>
      <Header
        action={PostAction.Edit}
        onClose={handleClose}
        groups={groups}
        onGroupToggle={onGroupToggle}
      />
      <CardContent className={classes.contentActions}>
        <Body />
        <Preview selectedGroup={selectedGroup} />
      </CardContent>
      <Divider className={classes.dividerActions} />
      <CardContent className={classes.contentOptions}>
        {renderOptions()}
        {renderAdminOptions()}
      </CardContent>
      {renderAdminCustomization()}
      <Divider className={classes.dividerButton} />
      <CardContent>
        <PostButton i18nKey="common:save" working={working} disabled={!isValid} />
      </CardContent>
    </Card>
  );
};

export default ManualEditor;
